import { isValidElement, ReactNode } from "react";
import {
  ACCESSORY_ITEMS,
  AvatarConfig,
  DEFAULT_AVATAR,
} from "../types/user";

export type RewardCategory = "avatar" | "badge" | "theme" | "special";
export type RewardRarity = "common" | "rare" | "epic" | "legendary";
export type RewardType = "reward" | "placeholder" | "mystery";

export interface NormalizedReward {
  id: string;
  level: number;
  name: string;
  description: string;
  category: RewardCategory;
  icon: string;
  image?: string;
  badgeImage?: string;
  overlayImage?: string;
  rarity: RewardRarity;
  accessoryId?: string;
  tone: "teal" | "sunset" | "sky" | "placeholder";
  type: RewardType;
  claimed: boolean;
  equipped: boolean;
    // ── Canje (agregado 01/09/2026) ──
  redeemEnabled: boolean;
  redeemTitle: string;
  redeemInstructions: string;
  redeemImage?: string;
}

export interface EquippedRewardState {
  badgeId?: string;
  frameItemId?: string;
}

const CLAIMED_REWARD_STORAGE_KEY = "battlePassClaimedRewards";
const EQUIPPED_REWARD_STORAGE_KEY = "battlePassEquippedRewards";
const IMAGE_SOURCE_PATTERN = /^(https?:\/\/|data:image\/|\/)|\.(png|jpe?g|gif|webp|svg)$/i;

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const asString = (value: unknown, fallback = ""): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return String(value);
  }

  return fallback;
};

const asNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return fallback;
};

const asBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return ["true", "1", "yes", "si", "sí", "equipado", "obtenido", "completado"].includes(
      value.trim().toLowerCase(),
    );
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return false;
};

const asArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      return [];
    }
  }

  return [];
};

const normalizeCategory = (value: string, accessoryId?: string): RewardCategory => {
  const normalizedValue = value.toLowerCase();

  if (
    normalizedValue.includes("badge") ||
    normalizedValue.includes("insign")
  ) {
    return "badge";
  }

  if (
    accessoryId ||
    normalizedValue.includes("avatar") ||
    normalizedValue.includes("hat") ||
    normalizedValue.includes("sombr") ||
    normalizedValue.includes("ropa") ||
    normalizedValue.includes("clothing") ||
    normalizedValue.includes("hair") ||
    normalizedValue.includes("peinado")
  ) {
    return "avatar";
  }

  if (normalizedValue.includes("theme") || normalizedValue.includes("tema")) {
    return "theme";
  }

  return "special";
};

const normalizeRarity = (value: string): RewardRarity => {
  const normalizedValue = value.toLowerCase();

  if (normalizedValue.includes("legend")) {
    return "legendary";
  }

  if (normalizedValue.includes("epic")) {
    return "epic";
  }

  if (normalizedValue.includes("rare")) {
    return "rare";
  }

  return "common";
};

const normalizeTone = (value: string): NormalizedReward["tone"] => {
  const normalizedValue = value.toLowerCase();

  if (normalizedValue.includes("teal")) {
    return "teal";
  }

  if (normalizedValue.includes("sun") || normalizedValue.includes("gold")) {
    return "sunset";
  }

  if (normalizedValue.includes("sky") || normalizedValue.includes("blue")) {
    return "sky";
  }

  return "placeholder";
};

const normalizeRewardType = (
  explicitType: string,
  name: string,
  icon: string,
  image?: string,
): RewardType => {
  const normalizedType = explicitType.toLowerCase();

  if (normalizedType === "placeholder" || (!name && !icon && !image)) {
    return "placeholder";
  }

  if (
    normalizedType === "mystery" ||
    name.toLowerCase().includes("sorpresa") ||
    icon === "?"
  ) {
    return "mystery";
  }

  return "reward";
};

export const isImageSource = (value?: string) => Boolean(value && IMAGE_SOURCE_PATTERN.test(value));

export const renderLooseNode = (
  value: unknown,
  fallback: ReactNode = null,
): ReactNode => {
  if (isValidElement(value)) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  return fallback;
};

export const normalizeRewardCollection = (source: unknown): NormalizedReward[] => {
  return asArray(source)
      .map((entry, index): NormalizedReward | null => {
      const record = asRecord(entry);

      if (!record) {
        const scalarValue = asString(entry, "");
        if (!scalarValue) {
          return null;
        }

        return {
          id: `reward-${index + 1}`,
          level: index + 1,
          name: scalarValue,
          description: "",
          category: "special" as RewardCategory,
          icon: scalarValue,
          rarity: "common" as RewardRarity,
          tone: "placeholder" as NormalizedReward["tone"],
          type: "reward" as RewardType,
          claimed: true,
          equipped: false,
          redeemEnabled: false,
          redeemTitle: "",
          redeemInstructions: "",
        };
          
      }

      const accessoryId = asString(
        record.AccessoryId ??
          record.accessoryId ??
          record.ItemId ??
          record.itemId ??
          record.accessory,
      );
      const icon = asString(
        record.Icon ??
          record.icon ??
          record.Icono ??
          record.icono ??
          record.Emoji ??
          record.emoji,
      );
      const image = asString(
        record.Image ??
          record.image ??
          record.Imagen ??
          record.imagen ??
          record.PreviewImage ??
          record.previewImage ??
          record.preview,
      );
      const badgeImage = asString(
        record.BadgeImage ??
          record.badgeImage ??
          record.InsigniaImagen ??
          record.insigniaImagen,
      );
      const overlayImage = asString(
        record.OverlayImage ??
          record.overlayImage ??
          record.FrameImage ??
          record.frameImage ??
          record.AvatarImage ??
          record.avatarImage,
      );
      const name = asString(
        record.Name ??
          record.name ??
          record.Nombre ??
          record.nombre ??
          record.Title ??
          record.title ??
          record.Titulo ??
          record.titulo,
        `Recompensa ${index + 1}`,
      );
      const description = asString(
        record.Description ??
          record.description ??
          record.Descripcion ??
          record.descripcion ??
          record.Cuerpo ??
          record.cuerpo,
      );
      const rawStatus = asString(record.Status ?? record.status);

      const redeemInstructions = asString(
        record.RedeemInstructions ??
          record.redeemInstructions ??
          record.Canje ??
          record.canje,
      );
      const redeemImage = asString(
        record.RedeemImage ??
          record.redeemImage ??
          record.CanjeImagen ??
          record.canjeImagen ??
          record.QR ??
          record.qr,
      );
      const redeemTitle = asString(
        record.RedeemTitle ?? record.redeemTitle ?? record.CanjeTitulo,
      );
      
      return {
        id: asString(record.id ?? record.Id ?? record.ID, `reward-${index + 1}`),
        level: asNumber(
          record.Level ?? record.level ?? record.Nivel ?? record.nivel,
          index + 1,
        ),
        name,
        description,
        category: normalizeCategory(
          asString(
            record.Category ??
              record.category ??
              record.Tipo ??
              record.tipo ??
              record.Type ??
              record.type,
          ),
          accessoryId,
        ),
        icon,
        image: image || undefined,
        badgeImage: badgeImage || undefined,
        overlayImage: overlayImage || undefined,
        rarity: normalizeRarity(
          asString(record.Rarity ?? record.rarity ?? record.Rareza ?? record.rareza),
        ),
        accessoryId: accessoryId || undefined,
        tone: normalizeTone(asString(record.Tone ?? record.tone)),
        type: normalizeRewardType(
          asString(record.RewardType ?? record.rewardType ?? record.type),
          name,
          icon,
          image || badgeImage || overlayImage,
        ),
        claimed:
          asBoolean(record.Claimed ?? record.claimed ?? record.Obtenido ?? record.obtenido) ||
          ["completado", "obtenido", "claimed", "unlocked"].includes(rawStatus.toLowerCase()),
                equipped:
          asBoolean(record.Equipped ?? record.equipped ?? record.Equipado ?? record.equipado) ||
          ["equipado", "equipped"].includes(rawStatus.toLowerCase()),
        // Si alguien cargó instrucciones o imagen pero se olvidó el switch,
        // igual mostramos el botón: es lo que el admin quiso hacer.
        redeemEnabled:
          asBoolean(record.RedeemEnabled ?? record.redeemEnabled) ||
          Boolean(redeemInstructions || redeemImage),
        redeemTitle,
        redeemInstructions,
        redeemImage: redeemImage || undefined,
      };
    })
    .filter((reward): reward is NormalizedReward => Boolean(reward));
};

export const loadAvatarConfig = (): AvatarConfig => {
  const savedAvatar = localStorage.getItem("userAvatar");
  if (!savedAvatar) {
    return DEFAULT_AVATAR;
  }

  try {
    return JSON.parse(savedAvatar) as AvatarConfig;
  } catch {
    return DEFAULT_AVATAR;
  }
};

export const loadClaimedRewardIds = (): string[] => {
  const savedRewards = localStorage.getItem(CLAIMED_REWARD_STORAGE_KEY);
  if (!savedRewards) {
    return ["1"];
  }

  try {
    const parsedRewards = JSON.parse(savedRewards);
    return asArray(parsedRewards).map((rewardId) => asString(rewardId)).filter(Boolean);
  } catch {
    return ["1"];
  }
};

export const saveClaimedRewardIds = (rewardIds: string[]) => {
  localStorage.setItem(CLAIMED_REWARD_STORAGE_KEY, JSON.stringify(rewardIds));
};

export const loadEquippedRewardState = (): EquippedRewardState => {
  const savedState = localStorage.getItem(EQUIPPED_REWARD_STORAGE_KEY);
  if (!savedState) {
    return {};
  }

  try {
    const parsedState = JSON.parse(savedState) as EquippedRewardState;
    return {
      badgeId: parsedState.badgeId,
      frameItemId: parsedState.frameItemId,
    };
  } catch {
    return {};
  }
};

export const saveEquippedRewardState = (state: EquippedRewardState) => {
  localStorage.setItem(EQUIPPED_REWARD_STORAGE_KEY, JSON.stringify(state));
};

export const getAccessoryFromReward = (reward?: NormalizedReward | null) => {
  if (!reward?.accessoryId) {
    return null;
  }

  return ACCESSORY_ITEMS.find((item) => item.id === reward.accessoryId) ?? null;
};

export const getEquippedHatAccessory = (avatarConfig: AvatarConfig) => {
  const hatId = avatarConfig.accessories.hat;
  if (!hatId) {
    return null;
  }

  return ACCESSORY_ITEMS.find((item) => item.id === hatId) ?? null;
};

export const getRewardBadgeVisualValue = (reward?: NormalizedReward | null) => {
  if (!reward) {
    return "";
  }

  return reward.badgeImage || reward.image || reward.icon;
};

export const getRewardOverlayVisualValue = (reward?: NormalizedReward | null) => {
  if (!reward) {
    return "";
  }

  return reward.overlayImage || reward.image || getAccessoryFromReward(reward)?.image || reward.icon;
};

export const getEquippedBadgeReward = (
  rewards: NormalizedReward[],
  equippedState?: EquippedRewardState,
) => {
  if (equippedState?.badgeId) {
    return rewards.find((reward) => reward.id === equippedState.badgeId) ?? null;
  }

  return rewards.find((reward) => reward.category === "badge" && reward.equipped) ?? null;
};

export const getEquippedFrameReward = (
  rewards: NormalizedReward[],
  equippedState?: EquippedRewardState,
) => {
  if (equippedState?.frameItemId) {
    return rewards.find((reward) => reward.id === equippedState.frameItemId) ?? null;
  }

  return rewards.find((reward) => reward.category === "avatar" && reward.equipped) ?? null;
};

export const isRewardEquipable = (reward: NormalizedReward) =>
  reward.type !== "placeholder" &&
  (reward.category === "badge" || reward.category === "avatar");

export const chunkRewards = <T,>(rewards: T[], size: number) => {
  const normalizedSize = Math.max(1, size);
  const chunks: T[][] = [];

  for (let index = 0; index < rewards.length; index += normalizedSize) {
    chunks.push(rewards.slice(index, index + normalizedSize));
  }

  return chunks;
};

// ══════════════════════════════════════════════════════════════
// CATÁLOGO COMPARTIDO DE RECOMPENSAS (agregado 26/08/2026)
//
// Antes cada usuario tenía su propia copia completa de las
// recompensas (Name/Description/Image/Level/etc.) en su documento
// de Firestore. Eso obligaba a editar usuario por usuario para
// cambiar algo que en realidad es igual para todos.
//
// Ahora las recompensas "reales" viven en una colección compartida
// (RecompensasCatalogo, ver firebase-config.ts) que se edita una
// sola vez desde el panel de Admin. Cada usuario solo guarda un
// estado liviano por recompensa: si la reclamó y si la tiene
// equipada, referenciando el id del catálogo (RewardId).
//
// Esta función junta ambas cosas para que el resto de la app (que
// ya sabe trabajar con NormalizedReward[]) no tenga que cambiar de
// forma: recibe el catálogo ya normalizado + el estado crudo del
// usuario, y devuelve el catálogo con claimed/equipped pisados por
// lo que el usuario efectivamente tiene.
// ══════════════════════════════════════════════════════════════
export const mergeRewardsWithUserState = (
  catalog: NormalizedReward[],
  userState: unknown,
): NormalizedReward[] => {
  const stateByRewardId = new Map<string, { claimed: boolean; equipped: boolean }>();

  asArray(userState).forEach((raw) => {
    const record = asRecord(raw);
    if (!record) return;

    const rewardId = asString(record.RewardId ?? record.rewardId ?? record.id);
    if (!rewardId) return;

    stateByRewardId.set(rewardId, {
      claimed: asBoolean(record.Claimed ?? record.claimed),
      equipped: asBoolean(record.Equipped ?? record.equipped),
    });
  });

  return catalog.map((reward) => {
    const state = stateByRewardId.get(reward.id);
    if (!state) {
      return { ...reward, claimed: false, equipped: false };
    }

    return { ...reward, claimed: state.claimed, equipped: state.equipped };
  });
};
